// Visitor profile editor — inline panel inside the Conversation view.
// Lets the agent see and correct the name / email / phone the visitor
// provided in the pre-chat form, or fill them in if the visitor skipped.
//
// Phase 5 will persist these edits to the database and (optionally) sync
// back to the widget pre-chat form for the visitor's next visit.

import { useEffect, useMemo, useState } from 'react';
import type { VisitorId } from '@prakash-software/shared';
import { useVisitorsStore } from '../stores/visitors';
import styles from './VisitorProfile.module.css';

export function VisitorProfile({ visitorId }: { visitorId: VisitorId }) {
  const rowsMap = useVisitorsStore((s) => s.rows);
  const row = useMemo(() => rowsMap.get(visitorId), [rowsMap, visitorId]);
  const editIdentity = useVisitorsStore((s) => s.editIdentity);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({
    name: row?.identity.name ?? '',
    email: row?.identity.email ?? '',
    phone: row?.identity.phone ?? '',
  });
  const [emailError, setEmailError] = useState<string | null>(null);

  // Re-sync draft when the underlying row changes (e.g. the visitor updates
  // their own profile mid-session via the pre-chat form — unlikely but possible).
  useEffect(() => {
    if (!editing && row) {
      setDraft({
        name: row.identity.name ?? '',
        email: row.identity.email ?? '',
        phone: row.identity.phone ?? '',
      });
    }
  }, [row, editing]);

  if (!row) return null;

  const hasAny =
    row.identity.name || row.identity.email || row.identity.phone;

  function startEdit() {
    setDraft({
      name: row?.identity.name ?? '',
      email: row?.identity.email ?? '',
      phone: row?.identity.phone ?? '',
    });
    setEmailError(null);
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setEmailError(null);
  }

  async function save() {
    if (draft.email && !isValidEmail(draft.email)) {
      setEmailError('That doesn’t look like a valid email.');
      return;
    }
    const result = await editIdentity(visitorId, draft);
    if (result.ok) {
      setEditing(false);
      setEmailError(null);
    } else {
      setEmailError(
        result.reason === 'forbidden'
          ? 'You can’t edit this visitor (different site).'
          : result.reason === 'bad-input'
            ? 'Server rejected those values.'
            : 'Couldn’t save — try again.',
      );
    }
  }

  return (
    <section className={styles.root} aria-label="Visitor profile">
      {editing ? (
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
          noValidate
        >
          <Field
            label="Name"
            value={draft.name}
            onChange={(v) => setDraft((d) => ({ ...d, name: v }))}
            autoFocus
            placeholder="Not provided"
          />
          <Field
            label="Email"
            value={draft.email}
            onChange={(v) => setDraft((d) => ({ ...d, email: v }))}
            type="email"
            placeholder="visitor@example.com"
            error={emailError}
          />
          <Field
            label="Phone"
            value={draft.phone}
            onChange={(v) => setDraft((d) => ({ ...d, phone: v }))}
            type="tel"
            placeholder="+91 …"
          />
          <div className={styles.actions}>
            <button type="submit" className={styles.btnPrimary}>
              Save
            </button>
            <button type="button" className={styles.btnGhost} onClick={cancel}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className={styles.readOnly}>
          {hasAny ? (
            <dl className={styles.dl}>
              {row.identity.name ? <Term label="Name">{row.identity.name}</Term> : null}
              {row.identity.email ? (
                <Term label="Email">
                  <a href={`mailto:${row.identity.email}`}>{row.identity.email}</a>
                </Term>
              ) : null}
              {row.identity.phone ? (
                <Term label="Phone">
                  <a href={`tel:${row.identity.phone}`}>{row.identity.phone}</a>
                </Term>
              ) : null}
            </dl>
          ) : (
            <p className={styles.empty}>
              The visitor skipped the pre-chat form. Click <strong>Edit</strong> to record what
              they tell you in chat.
            </p>
          )}
          <button type="button" className={styles.btnEdit} onClick={startEdit}>
            {hasAny ? 'Edit' : 'Add details'}
          </button>
        </div>
      )}
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  autoFocus,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoFocus?: boolean;
  error?: string | null;
}) {
  const id = `vp-${label.toLowerCase()}`;
  return (
    <label className={styles.field} htmlFor={id}>
      <span className={styles.fieldLabel}>{label}</span>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className={styles.fieldInput}
        aria-invalid={!!error}
      />
      {error ? <span className={styles.fieldError}>{error}</span> : null}
    </label>
  );
}

function Term({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className={styles.dt}>{label}</dt>
      <dd className={styles.dd}>{children}</dd>
    </>
  );
}

const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
function isValidEmail(value: string): boolean {
  if (!value) return false;
  if (value.length > 254) return false;
  return EMAIL_RE.test(value.trim());
}
